import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';

import type { OwnProps as BoardLockedModalProps } from 'af-root/scenes/Company/ScheduleBoard/Shared/BoardLockedModal';
import BoardLockedModal from 'af-root/scenes/Company/ScheduleBoard/Shared/BoardLockedModal';
import FilterModal from 'af-root/scenes/Company/ScheduleBoard/Shared/FilterModal';
import CrewsInfo from 'af-root/scenes/Company/ScheduleBoard/Shared/Header/CrewsInfo';
import NotifyAllParticipantsModal from 'af-root/scenes/Company/ScheduleBoard/Shared/NotifyAllParticipantsModal';
import ScheduleBoardTemplateNotifyModal from 'af-root/scenes/Company/ScheduleBoard/Shared/ScheduleBoardTemplateNotifyModal';
import WorkOrderCopyModal from 'af-root/scenes/Company/WorkOrders/Table/OrderCopyForm';

import type { W_Notification_FindExpectedNotifyResult_VM } from 'ab-api/web/notification/findExpectedNotifyResult';
import type { W_WorkOrder_WorkOrderNotifyLabor_VM_WorkOrder } from 'ab-api/web/workOrder/findNotificationsForEmployees';

import type { DailyPerDiemTipViewModel } from 'ab-viewModels/dailyPerDiemTip.viewModel';
import type { DailyTipViewModel } from 'ab-viewModels/dailyTip.viewModel';
import type { NotificationStatusByEmployee, NotificationStatusByTemporaryEmployee } from 'ab-viewModels/notification.viewModel';
import type ScheduleBoardNightShiftWorkOrder from 'ab-viewModels/scheduleBoardNightShiftWorkOrder.viewModel';

import type { DailyMessageRequestModel } from 'ab-requestModels/dailyMessage.requestModel';
import type ScheduleBoardSetPerDiemForWorkOrdersRequestModel from 'ab-requestModels/scheduleBoardSetPerDiemsForWorkOrder.requestModel';
import type WorkOrderCopyRM from 'ab-requestModels/workOrder/copyWorkOrders.requestModel';

import type { ScheduleBoardWorkOrderResourceLookupsViewModel } from 'ab-socketModels/viewModels/scheduleBoard/scheduleBoardResourceLookup.viewModel';
import type { ScheduleBoardWorkOrdersViewModel } from 'ab-socketModels/viewModels/scheduleBoard/scheduleBoardWorkOrder.viewModel';
import type ScheduleBoardWorkOrderViewModel from 'ab-socketModels/viewModels/scheduleBoard/scheduleBoardWorkOrder.viewModel';

import { isAllowed } from '@acceligentllc/shared/utils/auth';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { FileType } from '@acceligentllc/shared/enums/fileType';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { WorkOrderStatus } from '@acceligentllc/shared/enums/workOrderStatus';
import { WorkRequestStatus } from '@acceligentllc/shared/enums/workRequestStatus';

import type { RootState } from 'af-reducers';

import { debounce } from 'af-utils/actions.util';
import { captureDailyViewScheduleBoard } from 'af-utils/screenshot.util';
import socket from 'af-utils/socket.util';
import { convertWorkDays } from 'af-utils/workDay.util';
import * as WorkOrderUtils from 'af-utils/workOrder.util';

import PagePermissions from 'ab-enums/pagePermissions.enum';
import ScheduleBoardView from 'ab-enums/scheduleBoardView.enum';
import SocketEvent from 'ab-enums/socketEvent.enum';

import { EXPORT_SCHEDULE_BOARD, WORK_ORDER_FORM, DAILY_TIP_FORM, DAILY_PER_DIEM_TIP_FORM, SET_PER_DIEM_FOR_WORK_ORDERS } from 'af-constants/reduxForms';
import { SCREEN_BREAKPOINT_XL } from 'af-constants/values';

import { SEARCH_DELAY } from 'ab-common/constants/value';

import { Button, SearchInput } from '@acceligentllc/storybook';

import getDailyTipModel from 'af-components/SharedForms/DailyTipModal';

import type { SelectedWorkOrderModel, BaseWorkOrderModel, WarningWorkOrderModel } from 'af-models/scheduleBoard/scheduleBoard.models';

import * as DailyPerDiemTipActions from 'af-actions/dailyPerDiemTip';
import * as DailyTipActions from 'af-actions/dailyTip';
import * as NotifyActions from 'af-actions/notify';
import * as ScheduleBoardActions from 'af-actions/scheduleBoard';
import * as WorkOrderActions from 'af-actions/workOrder';

import AddPerDiemModal from '../AddPerDiemModal';
import ActionsMenu from './ActionsMenu';
import { InvalidWorkOrderModalReasons, isValidScheduleBoardWorkOrder } from './helpers';
import NightShiftsButton from './NightShiftsButton';
import NightShiftWorkOrdersModal from './NightShiftWorkOrdersModal';
import SelectMultipleButton from './SelectMultipleButton';
import ShowAddressNotesButton from './ShowAddressNotesButton';
import ShowNotesButton from './ShowNotesButton';
import SortMenu from './SortMenu';
import type { EmployeeAssignments, TemporaryEmployeeAssignments } from './types';
import WarningModal from './WarningModal';
import ExpectedNotifyResultModal from '../ExpectedNotifyResultModal';

interface OwnProps {
	companyName: string;
	orgAlias: string;
	isBoardLoading: boolean;
	refreshOrdersOnCopy?: (workOrderIds: number[]) => void;
}

type Props = OwnProps & CustomRouteComponentProps & ConnectedProps<typeof connector>;

interface State {
	addPerDiemModalVisible: boolean;
	boardLockedModalProps: BoardLockedModalProps;
	copyMultipleModalVisible: boolean;
	customTipId?: number;
	filterModalVisible: boolean;
	internalQuery: string;
	invalidWorkOrders: WarningWorkOrderModel[];
	lastFiveDailyTips: DailyTipViewModel[];
	lastFiveDailyPerDiemTips: DailyPerDiemTipViewModel[];
	notifyAllModalVisible: boolean;
	shouldReloadFilters: boolean;
	showDailyTipModal: boolean;
	showDailyPerDiemTipModal: boolean;
	showScheduleBoardTemplateModal: boolean;
	showWarningModal: boolean;
	notificationStatusByEmployee: NotificationStatusByEmployee;
	notificationStatusByTemporaryEmployee: NotificationStatusByTemporaryEmployee;
	publishedSelectedWorkOrders: ScheduleBoardWorkOrderViewModel[];
	unpublishedSelectedWorkOrders: ScheduleBoardWorkOrderViewModel[];
	canceledSelectedWorkOrders: ScheduleBoardWorkOrderViewModel[];
	lockedSelectedWorkOrders: ScheduleBoardWorkOrderViewModel[];
	workOrderResourceLookup: ScheduleBoardWorkOrderResourceLookupsViewModel;
	nightShiftWorkOrders: Nullable<ScheduleBoardNightShiftWorkOrder[]>;
	/** YYYY-MM-DD */
	nightShiftModalDueDate: Nullable<string>;
	workOrderNotifications: W_WorkOrder_WorkOrderNotifyLabor_VM_WorkOrder[];
	showExpectedNotificationResultModal: boolean;
	assignmentsThatMightNotBeNotified: Nullable<W_Notification_FindExpectedNotifyResult_VM>;
}

const initialBoardLockedModalProps: BoardLockedModalProps = {
	isOpen: false,
	reason: '',
	onEntered: undefined,
};

const DailyTipModal = getDailyTipModel(DAILY_TIP_FORM);
const DailyPerDiemTipModal = getDailyTipModel(DAILY_PER_DIEM_TIP_FORM);

class ScheduleBoardHeader extends React.Component<Props, State> {
	static defaultProps: Partial<Props> = {
		workDays: [],
		selectedDate: '',
		selectedWorkOrders: [],
		searchResultItems: [],
		employees: {},
		dailyTip: {} as DailyTipViewModel,
		dailyPerDiemTip: {} as DailyPerDiemTipViewModel,
		workOrders: {},
		participantsEmailAndSmsStatuses: {},
		assignedPublishedNotificationStatuses: {},
		assignedCanceledNotificationStatuses: {},
		canceledWorkOrderResourceLookup: {},
		notCanceledWorkOrderResourceLookup: {},
	};

	state: State = {
		copyMultipleModalVisible: false,
		filterModalVisible: false,
		addPerDiemModalVisible: false,
		shouldReloadFilters: false,
		internalQuery: '',
		notifyAllModalVisible: false,
		showWarningModal: false,
		showScheduleBoardTemplateModal: false,
		invalidWorkOrders: [],
		showDailyTipModal: false,
		showDailyPerDiemTipModal: false,
		lastFiveDailyTips: [],
		lastFiveDailyPerDiemTips: [],
		boardLockedModalProps: initialBoardLockedModalProps,
		notificationStatusByEmployee: { ...this.props.assignedPublishedNotificationStatuses, ...this.props.assignedCanceledNotificationStatuses },
		notificationStatusByTemporaryEmployee: {
			...(this.props.assignedPublishedTempLaborNotificationStatuses ?? {}),
			...(this.props.assignedCanceledTempLaborNotificationStatuses ?? {}),
		},
		publishedSelectedWorkOrders: [],
		unpublishedSelectedWorkOrders: [],
		canceledSelectedWorkOrders: [],
		lockedSelectedWorkOrders: [],
		workOrderResourceLookup: { ...this.props.canceledWorkOrderResourceLookup, ...this.props.notCanceledWorkOrderResourceLookup },
		nightShiftWorkOrders: null,
		nightShiftModalDueDate: null,
		workOrderNotifications: [],
		showExpectedNotificationResultModal: false,
		assignmentsThatMightNotBeNotified: null,
	};

	static computeSelectedWorkOrdersState(
		workOrders: ScheduleBoardWorkOrdersViewModel,
		selectedWorkOrders: SelectedWorkOrderModel[],
		isMultiSelectModeActive: boolean
	) {
		const publishedSelectedWorkOrders: ScheduleBoardWorkOrderViewModel[] = [];
		const unpublishedSelectedWorkOrders: ScheduleBoardWorkOrderViewModel[] = [];
		const canceledSelectedWorkOrders: ScheduleBoardWorkOrderViewModel[] = [];
		const lockedSelectedWorkOrders: ScheduleBoardWorkOrderViewModel[] = [];

		const isSelectedLookup = selectedWorkOrders.reduce((_dict: Record<number, true>, { id }: SelectedWorkOrderModel) => {
			_dict[id] = true;
			return _dict;
		}, {});

		Object.values(workOrders).forEach((_wo) => {
			const isSelected = isSelectedLookup[_wo.id];
			const multiSelectModeCondition = (!isMultiSelectModeActive || (isMultiSelectModeActive && isSelected));

			if (_wo.status === WorkOrderStatus.LOCKED && multiSelectModeCondition) {
				lockedSelectedWorkOrders.push(_wo);
			} else if (_wo.status === WorkOrderStatus.PUBLISHED && multiSelectModeCondition) {
				publishedSelectedWorkOrders.push(_wo);
			} else if (
				_wo.status !== WorkOrderStatus.PUBLISHED
				&& _wo.status !== WorkOrderStatus.LOCKED
				&& _wo.status !== WorkOrderStatus.CANCELED
				&& multiSelectModeCondition
			) {
				unpublishedSelectedWorkOrders.push(_wo);
			} else if (_wo.status === WorkOrderStatus.CANCELED && multiSelectModeCondition) {
				canceledSelectedWorkOrders.push(_wo);
			}
		});

		return {
			publishedSelectedWorkOrders,
			unpublishedSelectedWorkOrders,
			canceledSelectedWorkOrders,
			lockedSelectedWorkOrders,
		};
	}

	componentDidMount(): void {
		const {
			workOrders,
			selectedWorkOrders,
			scheduleBoardView,
			isMultiSelectModeActive,
		} = this.props;

		if (scheduleBoardView === ScheduleBoardView.DAILY_VIEW && !!workOrders) {
			this.setState(() => ScheduleBoardHeader.computeSelectedWorkOrdersState(workOrders, selectedWorkOrders, isMultiSelectModeActive));
		}
	}

	componentDidUpdate(prevProps: Props) {
		const { selectedDate: prevDate } = prevProps;
		const {
			selectedDate,
			updateScheduleBoardQuery,
			deactivateMultiSelectMode,
			searchResultItems,
			assignedPublishedNotificationStatuses,
			assignedCanceledNotificationStatuses,
			assignedPublishedTempLaborNotificationStatuses,
			assignedCanceledTempLaborNotificationStatuses,
			workOrders,
			selectedWorkOrders,
			scheduleBoardView,
			isMultiSelectModeActive,
			notCanceledWorkOrderResourceLookup,
			canceledWorkOrderResourceLookup,
		} = this.props;
		const {
			publishedSelectedWorkOrders,
			unpublishedSelectedWorkOrders,
			canceledSelectedWorkOrders,
		} = this.state;

		if (
			prevProps.assignedPublishedNotificationStatuses !== assignedPublishedNotificationStatuses
			|| prevProps.assignedCanceledNotificationStatuses !== assignedCanceledNotificationStatuses
		) {
			this.setState(() => ({ notificationStatusByEmployee: { ...assignedPublishedNotificationStatuses, ...assignedCanceledNotificationStatuses } }));
		}

		if (
			prevProps.assignedPublishedTempLaborNotificationStatuses !== assignedPublishedTempLaborNotificationStatuses
			|| prevProps.assignedCanceledTempLaborNotificationStatuses !== assignedCanceledTempLaborNotificationStatuses
		) {
			this.setState(() => ({
				notificationStatusByTemporaryEmployee: {
					...assignedPublishedTempLaborNotificationStatuses,
					...assignedCanceledTempLaborNotificationStatuses,
				},
			}));
		}

		if (
			prevProps.canceledWorkOrderResourceLookup !== canceledWorkOrderResourceLookup
			|| prevProps.notCanceledWorkOrderResourceLookup !== notCanceledWorkOrderResourceLookup
		) {
			this.setState(() => ({ workOrderResourceLookup: { ...canceledWorkOrderResourceLookup, ...notCanceledWorkOrderResourceLookup } }));
		}

		if (
			scheduleBoardView === ScheduleBoardView.DAILY_VIEW
			&& !!workOrders
			&& (
				prevProps.scheduleBoardView !== scheduleBoardView
				|| prevProps.workOrders !== workOrders
				|| prevProps.selectedWorkOrders !== selectedWorkOrders
				|| prevProps.isMultiSelectModeActive !== isMultiSelectModeActive
			)
		) {
			this.setState(() => ScheduleBoardHeader.computeSelectedWorkOrdersState(workOrders, selectedWorkOrders, isMultiSelectModeActive));
		} else if (
			scheduleBoardView !== ScheduleBoardView.DAILY_VIEW
			&& (publishedSelectedWorkOrders.length + unpublishedSelectedWorkOrders.length + canceledSelectedWorkOrders.length) > 0
		) {
			this.setState(() => ({
				publishedSelectedWorkOrders: [],
				unpublishedSelectedWorkOrders: [],
				canceledSelectedWorkOrders: [],
			}));
		}

		const shouldReset: boolean = selectedDate && prevDate ? selectedDate.localeCompare(prevDate) !== 0 : !!prevDate;

		if (prevProps.searchResultItems !== searchResultItems) {
			this.scrollNodeIntoView(searchResultItems[0]);
		}

		if (shouldReset) {
			// reset state
			this.setState(() => ({ internalQuery: '' }), () => {
				updateScheduleBoardQuery('');
				deactivateMultiSelectMode();
			});
		}
	}

	componentWillUnmount() {
		const { updateScheduleBoardQuery, deactivateMultiSelectMode, clearFilters } = this.props;
		updateScheduleBoardQuery('');
		deactivateMultiSelectMode();
		clearFilters();
	}

	toggleMultiSelectModeActive = () => {
		const { isMultiSelectModeActive, deactivateMultiSelectMode, activateMultiSelectMode } = this.props;
		const action = isMultiSelectModeActive ? deactivateMultiSelectMode : activateMultiSelectMode;
		action();
	};

	toggleShowNotesMode = () => {
		const { isShowNotesActive, setShowNotesMode } = this.props;
		setShowNotesMode(!isShowNotesActive);
	};

	toggleShowAddressNotesMode = () => {
		const { isShowAddressNotesActive, setShowAddressNotesMode } = this.props;
		setShowAddressNotesMode(!isShowAddressNotesActive);
	};

	onQueryChanged = (query: string = '') => {
		const { updateScheduleBoardQuery } = this.props;
		updateScheduleBoardQuery(query);
	};

	// eslint-disable-next-line @typescript-eslint/member-ordering
	onQueryChangedDebounced = debounce(this.onQueryChanged, SEARCH_DELAY);

	onQueryChangedDebouncedEventHandler = (event: Metadata = {}) => {
		const query = event?.target?.value || '';
		this.setState(() => ({ internalQuery: query }), () => this.onQueryChangedDebounced(this.state.internalQuery));
	};

	setPerDiemForWorkOrders = async () => {
		const { selectedWorkOrders, selectedDate } = this.props;

		if (!selectedDate) {
			throw new Error('No date selected');
		}

		const workOrderIds = selectedWorkOrders.map((_workOrder) => _workOrder.id);
		const data: ScheduleBoardSetPerDiemForWorkOrdersRequestModel = {
			workOrderIds,
			dueDate: selectedDate,
		};
		await socket.connection?.emitWithPromise(
			SocketEvent.V2.FE.SCHEDULE_BOARD.SET_PER_DIEM_FOR_WORK_ORDERS,
			{ submitting: SET_PER_DIEM_FOR_WORK_ORDERS },
			data
		);
		this.closeAddPerDiemModal();
	};

	openAddPerDiemModal = () => this.setState(() => ({ addPerDiemModalVisible: true }));

	closeAddPerDiemModal = () => this.setState(() => ({ addPerDiemModalVisible: false }));

	openCopyMultipleModal = () => {
		const {
			isMultiSelectModeActive,
			selectedDate: _selectedDate,
			workDays,
			reinitializeCopyForm,
			selectedWorkOrders,
			workOrders,
		} = this.props;

		const selectedDate = TimeUtils.parseMoment(_selectedDate, TimeFormat.DATE_ONLY);
		const _workOrders: BaseWorkOrderModel[] = isMultiSelectModeActive ? selectedWorkOrders : Object.values(workOrders ?? {});

		if (!selectedDate || !_selectedDate) {
			throw new Error('No date selected');
		}

		const validityCondition = (wo: BaseWorkOrderModel) => {
			if (workOrders?.[wo.code]?.jobStatus === WorkRequestStatus.FINISHED) {
				return 'finishedOrders';
			} else if (workOrders?.[wo.code]?.status === WorkOrderStatus.CANCELED) {
				return 'cancelledOrders';
			} else {
				return 'validOrders';
			}
		};

		const { validOrders, finishedOrders, cancelledOrders } = WorkOrderUtils.groupWorkOrdersByValidity(_workOrders, validityCondition);

		reinitializeCopyForm(
			selectedDate.clone().add(1, 'days').format(TimeFormat.DATE_ONLY),
			_selectedDate,
			convertWorkDays(workDays),
			validOrders,
			finishedOrders,
			cancelledOrders
		);
		this.setState(() => ({ copyMultipleModalVisible: true }));
	};

	closeCopyMultipleModal = () => {
		this.setState(() => ({ copyMultipleModalVisible: false }));
	};

	openFilterModal = () => {
		this.setState(() => ({ filterModalVisible: true, shouldReloadFilters: false }));
	};

	closeFilterModal = () => {
		this.setState(() => ({ filterModalVisible: false }));
	};

	clearFilter = () => {
		const { clearFilters } = this.props;
		this.setState(() => ({ shouldReloadFilters: true }));
		clearFilters();
	};

	openNotifyAllModal = async () => {
		const { findAllWorkOrdersWithNotificationsByDueDate, selectedDate, selectedWorkOrders } = this.props;

		const workOrderIds = selectedWorkOrders.map((_wo) => _wo.id);

		if (!selectedDate) {
			throw new Error('No date selected');
		}

		this.setState(() => ({ notifyAllModalVisible: true }));
		const notificationLengths = await findAllWorkOrdersWithNotificationsByDueDate(selectedDate, workOrderIds);
		this.setState(() => ({ workOrderNotifications: notificationLengths }));
	};

	openExpectedNotificationResultModal = () => {
		this.setState(() => ({ showExpectedNotificationResultModal: true }));
	};

	closeExpectedNotificationResultModal = () => {
		this.setState(() => ({ showExpectedNotificationResultModal: false, assignmentsThatMightNotBeNotified: null }));
	};

	openScheduleBoardTemplateNotifyModal = () => this.setState(() => ({ showScheduleBoardTemplateModal: true }));

	closeScheduleBoardTemplateNotifyModal = () => this.setState(() => ({ showScheduleBoardTemplateModal: false }));

	closeNotifyAllModal = () => {
		this.setState(() => ({ notifyAllModalVisible: false }));
	};

	getLongestNotificationMessage = () => {
		const notificationsLengths = this.state.workOrderNotifications.map((_wo) => _wo?.notificationTemplateLength ?? 0) ?? [];
		return Math.max(...notificationsLengths);
	};

	getPublishedWorkOrderList = (): (ScheduleBoardWorkOrderViewModel & ScheduleBoardWorkOrderViewModel)[] => {
		const { participantsEmailAndSmsStatuses } = this.props;
		const { publishedSelectedWorkOrders } = this.state;
		return publishedSelectedWorkOrders.map((_wo) => ({ ..._wo, participantsEmailAndSmsStatus: participantsEmailAndSmsStatuses[_wo.code] }));
	};

	getCanceledWorkOrderList = (): (ScheduleBoardWorkOrderViewModel & ScheduleBoardWorkOrderViewModel)[] => {
		const { participantsEmailAndSmsStatuses } = this.props;
		const { canceledSelectedWorkOrders } = this.state;
		return canceledSelectedWorkOrders.map((_wo) => ({ ..._wo, participantsEmailAndSmsStatus: participantsEmailAndSmsStatuses[_wo.code] }));
	};

	getUnPublishedWorkOrderList = () => {
		return this.state.unpublishedSelectedWorkOrders;
	};

	copyOrders = async (copyForm: WorkOrderCopyRM) => {
		const {
			copyWorkOrder,
			setWorkOrdersSelection,
			refreshOrdersOnCopy,
			isMultiSelectModeActive,
			selectedWorkOrders,
		} = this.props;
		let workOrderIds: number[] = [];
		if (isMultiSelectModeActive) {
			const expandedCopyForm: WorkOrderCopyRM = {
				...copyForm,
				selectedOrdersIds: selectedWorkOrders.map(({ id }) => id),
			};
			workOrderIds = await copyWorkOrder(expandedCopyForm);
			setWorkOrdersSelection();
		} else {
			workOrderIds = await copyWorkOrder(copyForm);
		}
		if (refreshOrdersOnCopy) {
			refreshOrdersOnCopy(workOrderIds);
		}
		this.closeCopyMultipleModal();
	};

	notifyParticipants = async (
		workOrderEmployeeIdsForSms: number[],
		workOrderEmployeeIdsForEmail: number[],
		workOrderTemporaryEmployeeIdsForEmail: number[],
		workOrderTemporaryEmployeeIdsForSms: number[],
		assignments: { employeeAssignments: EmployeeAssignments[]; temporaryEmployeeAssignments: TemporaryEmployeeAssignments[]; },
		message?: string,
		isEdited?: boolean
	) => {
		let customTipId: Nullable<number> = null;
		if (message) {
			customTipId = isEdited
				? await this.createCustomDailyTip(message)
				: this.props.dailyTip?.id ?? null;
		}
		const { sendTemplateNotification, dueDate: _dueDate } = this.props;

		const dueDate = _dueDate && TimeUtils.formatDate(_dueDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY);
		if (!dueDate) {
			throw new Error('Due date not provided');
		}

		await this.showExpectedNotifyResult(assignments);

		await sendTemplateNotification({
			workOrderEmployeeSmsIds: workOrderEmployeeIdsForSms,
			workOrderEmployeeEmailIds: workOrderEmployeeIdsForEmail,
			workOrderTempEmployeeSmsIds: workOrderTemporaryEmployeeIdsForSms,
			workOrderTempEmployeeEmailIds: workOrderTemporaryEmployeeIdsForEmail,
			dailyTipId: customTipId,
		});
	};

	scheduleAutoNotify = async (
		workOrderEmployeeIdsForSms: number[],
		workOrderEmployeeIdsForEmail: number[],
		workOrderTemporaryEmployeeIdsForEmail: number[],
		workOrderTemporaryEmployeeIdsForSms: number[],
		assignments: { employeeAssignments: EmployeeAssignments[]; temporaryEmployeeAssignments: TemporaryEmployeeAssignments[]; },
		message?: string
	) => {
		const { scheduleForAutoNotify, dueDate } = this.props;
		if (!dueDate) {
			throw new Error('Due date not provided');
		}

		await this.showExpectedNotifyResult(assignments);

		const customTipId = message ? await this.createCustomDailyTip(message) : undefined;
		await scheduleForAutoNotify({
			dueDate: TimeUtils.formatDate(dueDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY),
			workOrderEmployeeEmailIds: workOrderEmployeeIdsForEmail,
			workOrderEmployeeSmsIds: workOrderEmployeeIdsForSms,
			tempEmployeeEmailIds: workOrderTemporaryEmployeeIdsForEmail,
			tempEmployeeSmsIds: workOrderTemporaryEmployeeIdsForSms,
			dailyTipId: customTipId,
		});
	};

	createCustomDailyTip = async (message: string) => {
		const { createDailyTip, dueDate } = this.props;
		if (!dueDate) {
			throw new Error('Due date not provided');
		}

		const customTip = await createDailyTip(message, dueDate, true);
		return customTip.id;
	};

	exportAsXlsx = async (withCalculations: boolean, onlyCurrentDay: boolean) => {
		const { selectedDate, exportScheduleBoard } = this.props;
		if (!selectedDate) {
			throw new Error('Date not provided');
		}

		await exportScheduleBoard(selectedDate, withCalculations, onlyCurrentDay, FileType.XLSX);
	};

	exportAsPdf = async (withCalculations: boolean, onlyCurrentDay: boolean) => {
		const { selectedDate, exportScheduleBoard } = this.props;
		if (!selectedDate) {
			throw new Error('Date not provided');
		}

		await exportScheduleBoard(selectedDate, withCalculations, onlyCurrentDay, FileType.PDF);
	};

	captureBoardScreenshotBlockingAction = async () => {
		const { selectedDate, zoomLevel } = this.props;
		const filename = `Schedule_board_${selectedDate}`;
		await captureDailyViewScheduleBoard(SCREEN_BREAKPOINT_XL, filename, zoomLevel);
		this.setState(() => ({ boardLockedModalProps: initialBoardLockedModalProps }));
	};

	captureBoardScreenshot = () => {
		const { scheduleBoardView } = this.props;
		if (scheduleBoardView === ScheduleBoardView.DAILY_VIEW) {
			const boardLockedModalProps = {
				isOpen: true,
				reason: 'Capturing screenshot',
				onEntered: this.captureBoardScreenshotBlockingAction,
			};
			this.setState(() => ({ boardLockedModalProps }));
			// the capture screenshot logic is activated inside the modal for it to render properly
		}
	};

	showWarningModal = () => {
		this.setState(() => ({ showWarningModal: true }));
	};

	hideWarningModal = () => {
		this.setState(() => ({ showWarningModal: false }));
	};

	checkWorkOrders = () => {
		const { unpublishedSelectedWorkOrders } = this.state;

		const { valid, invalid } = unpublishedSelectedWorkOrders.reduce((_acc, _wo) => {
			const { isValid, errors } = isValidScheduleBoardWorkOrder(_wo);
			if (!isValid) {
				const reason = `Invalid, missing ${Object.keys(errors).map((_r) => InvalidWorkOrderModalReasons[_r]).join(', ')}`;

				_acc.invalid.push({ woCode: _wo.codeStripped, reason, color: _wo.crewTypeColor });
			} else if (_wo.locked) {
				_acc.invalid.push({ woCode: _wo.codeStripped, reason: 'Currently locked in edit mode', color: _wo.crewTypeColor });
			} else {
				_acc.valid.push(_wo.id);
			}
			return _acc;
		}, { valid: [], invalid: [] } as { valid: number[]; invalid: WarningWorkOrderModel[]; });

		this.setState(() => ({ invalidWorkOrders: invalid }));

		return { valid, invalid };
	};

	publishWorkOrders = async () => {
		const { publishWorkOrders } = this.props;
		const { publishedSelectedWorkOrders, canceledSelectedWorkOrders, lockedSelectedWorkOrders, showWarningModal } = this.state;
		const { valid, invalid } = this.checkWorkOrders();

		if (showWarningModal) {
			this.hideWarningModal();

			if (valid.length) {
				await publishWorkOrders(valid);
			}
		} else {
			if (invalid.length || publishedSelectedWorkOrders.length || canceledSelectedWorkOrders.length || lockedSelectedWorkOrders.length) {
				this.showWarningModal();
			} else {
				publishWorkOrders(valid);
			}
		}
	};

	getWarnings = () => {
		const { publishedSelectedWorkOrders, canceledSelectedWorkOrders, lockedSelectedWorkOrders, invalidWorkOrders } = this.state;
		const publishedWarning = publishedSelectedWorkOrders.map((_wo) => ({ woCode: _wo.codeStripped, reason: 'Already Published, Not Outdated', color: _wo.crewTypeColor }));
		const canceledWarning = canceledSelectedWorkOrders.map((_wo) => ({ woCode: _wo.codeStripped, reason: 'Canceled', color: _wo.crewTypeColor }));
		const lockedWarning = lockedSelectedWorkOrders.map((_wo) => ({ woCode: _wo.codeStripped, reason: 'Locked', color: _wo.crewTypeColor }));
		return [...publishedWarning, ...canceledWarning, ...lockedWarning, ...invalidWorkOrders];
	};

	openDailyTipModal = async () => {
		const { findLastFiveDailyTips } = this.props;
		const lastFive = await findLastFiveDailyTips();
		this.setState(() => ({ showDailyTipModal: true, lastFiveDailyTips: lastFive }));
	};

	hideDailyTipModal = () => {
		this.setState(() => ({ showDailyTipModal: false }));
	};

	submitDailyTip = (values: DailyMessageRequestModel) => {
		const { dueDate, createDailyTip } = this.props;
		if (!dueDate) {
			throw new Error('Due date not provided');
		}

		createDailyTip(values.message, dueDate, false);
		this.hideDailyTipModal();
	};

	openDailyPerDiemTipModal = async () => {
		const { findLastFiveDailyPerDiemTips } = this.props;
		const lastFive = await findLastFiveDailyPerDiemTips();
		this.setState(() => ({ showDailyPerDiemTipModal: true, lastFiveDailyPerDiemTips: lastFive }));
	};

	hideDailyPerDiemTipModal = () => {
		this.setState(() => ({ showDailyPerDiemTipModal: false, lastFiveDailyPerDiemTips: [] }));
	};

	submitDailyPerDiemTip = (values: DailyMessageRequestModel) => {
		const { dueDate, createDailyPerDiemTip } = this.props;
		if (!dueDate) {
			throw new Error('Due date not provided');
		}

		createDailyPerDiemTip(values.message, dueDate);
		this.hideDailyPerDiemTipModal();
	};

	clearSearch = () => {
		this.setState(
			() => ({ internalQuery: '' }),
			() => {
				const { updateScheduleBoardQuery } = this.props;
				updateScheduleBoardQuery('');
			}
		);
	};

	decrementHighlightedNodeIndex = () => {
		const { setActiveSearchItemIndex, activeSearchItemIndex, searchResultItems } = this.props;
		const newActiveSearchItemIndex = activeSearchItemIndex - 1;
		const activeNodeId = searchResultItems[newActiveSearchItemIndex];

		setActiveSearchItemIndex(newActiveSearchItemIndex);
		this.scrollNodeIntoView(activeNodeId);
	};

	incrementHighlightedNodeIndex = () => {
		const { setActiveSearchItemIndex, activeSearchItemIndex, searchResultItems } = this.props;
		const newActiveSearchItemIndex = activeSearchItemIndex + 1;
		const activeNodeId = searchResultItems[newActiveSearchItemIndex];

		setActiveSearchItemIndex(newActiveSearchItemIndex);
		this.scrollNodeIntoView(activeNodeId);
	};

	scrollNodeIntoView = (nodeId: string) => {
		const { scheduleBoardView } = this.props;
		const node = document.getElementById(nodeId);
		const expressionToDetermineContextOfContainerInWhichNodeResides = /search-item-(wo|employee|equipment)/;
		const result = expressionToDetermineContextOfContainerInWhichNodeResides.exec(nodeId);
		const containerContext = result?.[1];
		let containerThatNeedsToBeScrolled: Nullable<Element> = null;

		switch (containerContext) {
			case 'employee':
				containerThatNeedsToBeScrolled = document
					.getElementsByClassName('schedule-board-toolbar-column-wrapper employees')[0]
					.firstChild as Element;
				break;
			case 'equipment':
				containerThatNeedsToBeScrolled = document
					.getElementsByClassName('schedule-board-toolbar-column-wrapper equipment')[0]
					.firstChild as Element;
				break;
			case 'wo':
				if (scheduleBoardView === ScheduleBoardView.DAILY_VIEW) {
					containerThatNeedsToBeScrolled = document
						.getElementsByClassName('schedule-board-cards-container-wrapper')[0]
						.firstChild as Element;
				}
				break;
			default:
				return;
		}

		if (containerThatNeedsToBeScrolled && node) {
			// 30 px above the active node.
			const positionToScrollTo = containerThatNeedsToBeScrolled.scrollTop
				- containerThatNeedsToBeScrolled.getBoundingClientRect().top
				+ node.getBoundingClientRect().top - 30;

			containerThatNeedsToBeScrolled.scrollTo({ top: positionToScrollTo, behavior: 'smooth' });
		}
	};

	_closeNightShiftModal = () => this.setState(() => ({ nightShiftWorkOrders: null }));

	_handleNightShiftPress = async () => {
		const { dueDate, findNightShiftWorkOrders } = this.props;

		const today = dueDate && TimeUtils.parseDate(dueDate, TimeFormat.DATE_ONLY);
		const yesterday = today && TimeUtils.addDays(today, -1);
		const dbYesterdayDate = yesterday && TimeUtils.formatDate(yesterday, TimeFormat.DB_DATE_ONLY);

		const workOrders = dbYesterdayDate
			? await findNightShiftWorkOrders(dbYesterdayDate)
			: null;
		if (!workOrders) {
			return;
		}
		this.setState(() => ({ nightShiftWorkOrders: workOrders, nightShiftModalDueDate: dbYesterdayDate }));
	};

	showExpectedNotifyResult = async (
		event: { employeeAssignments: EmployeeAssignments[]; temporaryEmployeeAssignments: TemporaryEmployeeAssignments[]; }
	) => {
		const { findExpectedResultsForNotify } = this.props;
		const expectedNotifyResults = await findExpectedResultsForNotify(event);
		if (expectedNotifyResults.employeeAssignmentsThatWontReceiveNotification.flatMap((_en) => [..._en.employeeFullNames]).length
			|| expectedNotifyResults.temporaryEmployeeAssignmentsThatWontReceiveNotification.flatMap((_ten) => [..._ten.temporaryEmployeeFullNames]).length
		) {
			this.setState(() => ({ assignmentsThatMightNotBeNotified: expectedNotifyResults }));
			this.openExpectedNotificationResultModal();
		}
	};

	onQueryChangedHandler = (query: string) => {
		this.setState(() => ({ internalQuery: query }), () => this.onQueryChangedDebounced(this.state.internalQuery));
	};

	render() {
		const {
			autoNotifyAt,
			autoNotifyOnPublish,
			companyName,
			employees,
			temporaryEmployees,
			exporting,
			history,
			isAllowedToEditWorkOrders,
			isAllowedToManagePerDiems,
			isAllowedToViewProdData,
			isBoardLoading,
			isLoading,
			isMultiSelectModeActive,
			isShowNotesActive,
			isShowAddressNotesActive,
			notificationsDisabled,
			orgAlias,
			publishing,
			scheduleBoardView,
			selectedDate,
			selectedWorkOrders,
			dailyTip: { message: dailyTipMessage = '' } = {},
			dailyPerDiemTip: { message: dailyPerDiemTipMessage = '' } = {},
			initialCopyModalData,
			notifyTemporaryLabor,
			enableScheduledNotifications,
			workOrders,
			activeSearchItemIndex,
			searchResultItems,
		} = this.props;

		const {
			copyMultipleModalVisible,
			filterModalVisible,
			shouldReloadFilters,
			internalQuery,
			nightShiftWorkOrders,
			nightShiftModalDueDate,
			notifyAllModalVisible,
			addPerDiemModalVisible,
			showWarningModal,
			showDailyTipModal,
			showDailyPerDiemTipModal,
			showScheduleBoardTemplateModal,
			lastFiveDailyTips,
			lastFiveDailyPerDiemTips,
			boardLockedModalProps,
			notificationStatusByEmployee,
			notificationStatusByTemporaryEmployee,
			publishedSelectedWorkOrders,
			unpublishedSelectedWorkOrders,
			canceledSelectedWorkOrders,
			workOrderResourceLookup,
			assignmentsThatMightNotBeNotified,
			showExpectedNotificationResultModal,
		} = this.state;

		const isDailyView = scheduleBoardView === ScheduleBoardView.DAILY_VIEW;
		const isWeeklyView = scheduleBoardView === ScheduleBoardView.WEEKLY_VIEW;

		const isCopyDisabled = !isDailyView
			|| (isMultiSelectModeActive && (selectedWorkOrders.length === 0 || selectedWorkOrders.every((wo) => wo.isCancelled)));
		const isNotifyAllDisabled = !publishedSelectedWorkOrders.length && !canceledSelectedWorkOrders.length;
		const isPublishDisabled = !unpublishedSelectedWorkOrders.length;
		const isAddPerDiemDisabled = selectedWorkOrders.length === 0 || !isAllowedToManagePerDiems;

		const isCrewsInfoVisible = isDailyView && isAllowedToViewProdData;

		return (
			<div className="schedule-board-header__second">
				<div className="schedule-board-header__second__left">
					<div className="schedule-board-header__second__left__search">
						<SearchInput
							currentResult={searchResultItems.length ? activeSearchItemIndex + 1 : 0}
							name="query"
							onChange={this.onQueryChangedHandler}
							onNext={this.incrementHighlightedNodeIndex}
							onPrevious={this.decrementHighlightedNodeIndex}
							placeholder="Search"
							totalResults={searchResultItems.length}
							value={internalQuery}
						/>
					</div>
					<Button
						customStyleClass="schedule-board-header__second__left__filter"
						label="Filter"
						onClick={this.openFilterModal}
						style="secondary"
					/>
					<Button
						customStyleClass="schedule-board-header__second__left__filter"
						label="Clear"
						onClick={this.clearFilter}
						style="secondary"
					/>
					<SortMenu />
				</div>
				<div className="schedule-board-header__second__center">
					{isCrewsInfoVisible && <CrewsInfo dueDate={selectedDate!} />}
				</div>
				<div className="schedule-board-header__second__right">
					{
						isDailyView &&
						<NightShiftsButton onPress={this._handleNightShiftPress} />
					}
					{
						(isDailyView || isWeeklyView) &&
						<ShowNotesButton
							isShowNotesActive={isShowNotesActive}
							toggleShowNotesMode={this.toggleShowNotesMode}
						/>
					}
					{
						(isDailyView || isWeeklyView) &&
						<ShowAddressNotesButton
							isShowNotesActive={isShowAddressNotesActive}
							toggleShowNotesMode={this.toggleShowAddressNotesMode}
						/>
					}
					{
						isDailyView && isAllowedToEditWorkOrders &&
						<SelectMultipleButton
							isMultiSelectActive={isMultiSelectModeActive}
							toggleMultiSelect={this.toggleMultiSelectModeActive}
						/>
					}
					<ActionsMenu
						captureBoardScreenshot={this.captureBoardScreenshot}
						companyName={companyName}
						copyWorkOrders={this.openCopyMultipleModal}
						exportPdf={this.exportAsPdf}
						exportXlsx={this.exportAsXlsx}
						history={history}
						isAddPerDiemDisabled={isAddPerDiemDisabled}
						isAllowedToEdit={isAllowedToEditWorkOrders}
						isAllowedToViewProdData={isAllowedToViewProdData}
						isBoardLoading={isBoardLoading}
						isCopyDisabled={isCopyDisabled}
						isExporting={exporting}
						isMultiSelectActive={isMultiSelectModeActive}
						isNotifyDisabled={isNotifyAllDisabled}
						isNotifyShown={!notificationsDisabled}
						isPublishDisabled={isPublishDisabled}
						isPublishing={publishing}
						notifyWorkOrders={this.openNotifyAllModal}
						openAddPerDiemModal={this.openAddPerDiemModal}
						openDailyPerDiemTipModal={this.openDailyPerDiemTipModal}
						openDailyTipModal={this.openDailyTipModal}
						openSBTemplateNotificationModal={this.openScheduleBoardTemplateNotifyModal}
						orgAlias={orgAlias}
						publishWorkOrders={this.publishWorkOrders}
						scheduleBoardView={scheduleBoardView}
					/>
				</div>
				<AddPerDiemModal
					closeModal={this.closeAddPerDiemModal}
					modalVisible={addPerDiemModalVisible}
					onSave={this.setPerDiemForWorkOrders}
					selectedWorkOrders={selectedWorkOrders}
					workOrders={workOrders ?? {}}
				/>
				<BoardLockedModal {...boardLockedModalProps} />
				<WorkOrderCopyModal
					closeCopyModal={this.closeCopyMultipleModal}
					initialFormData={initialCopyModalData}
					isCopyMultipleModal={true}
					onSubmit={this.copyOrders}
					showCopyModal={copyMultipleModalVisible}
				/>
				<FilterModal
					closeFilterModal={this.closeFilterModal}
					modalVisible={filterModalVisible}
					shouldReloadFilters={shouldReloadFilters}
				/>
				<NotifyAllParticipantsModal
					autoNotifyAt={autoNotifyAt}
					autoNotifyOnPublish={autoNotifyOnPublish}
					canceledOrders={this.getCanceledWorkOrderList()}
					closeModal={this.closeNotifyAllModal}
					dailyTip={dailyTipMessage}
					dueDate={selectedDate}
					employeeMap={employees}
					enableScheduledNotifications={enableScheduledNotifications}
					isLoading={isLoading}
					notificationMessageLength={notifyAllModalVisible ? this.getLongestNotificationMessage() : 0}
					notificationStatusByEmployee={notificationStatusByEmployee}
					notificationStatusByTemporaryEmployee={notificationStatusByTemporaryEmployee}
					notifyParticipants={this.notifyParticipants}
					notifyTemporaryLabor={notifyTemporaryLabor}
					orders={this.getPublishedWorkOrderList()}
					scheduleAutoNotify={this.scheduleAutoNotify}
					showModal={notifyAllModalVisible}
					temporaryEmployeeMap={temporaryEmployees}
					unpublishedOrders={this.getUnPublishedWorkOrderList()}
					workOrderResourceLookup={workOrderResourceLookup}
				/>
				{assignmentsThatMightNotBeNotified &&
					<ExpectedNotifyResultModal
						closeModal={this.closeExpectedNotificationResultModal}
						expectedNotifyResult={assignmentsThatMightNotBeNotified}
						showModal={showExpectedNotificationResultModal && !notifyAllModalVisible}
					/>
				}
				<WarningModal
					closeModal={this.hideWarningModal}
					isMultiSelectModeActive={isMultiSelectModeActive}
					showWarning={showWarningModal}
					submit={this.publishWorkOrders}
					warnings={showWarningModal ? this.getWarnings() : []}
				/>
				{showDailyTipModal &&
					<DailyTipModal
						closeModal={this.hideDailyTipModal}
						initialValues={{ message: dailyTipMessage }}
						isLoading={isLoading}
						label="Daily Tip Message"
						modalVisible={showDailyTipModal}
						onSubmit={this.submitDailyTip}
						previousMessages={lastFiveDailyTips}
					/>
				}
				{showDailyPerDiemTipModal &&
					<DailyPerDiemTipModal
						closeModal={this.hideDailyPerDiemTipModal}
						initialValues={{ message: dailyPerDiemTipMessage }}
						isLoading={isLoading}
						label="Daily Per Diem Message"
						modalVisible={showDailyPerDiemTipModal}
						onSubmit={this.submitDailyPerDiemTip}
						previousMessages={lastFiveDailyPerDiemTips}
					/>
				}
				{isDailyView && selectedDate &&
					<ScheduleBoardTemplateNotifyModal
						closeModal={this.closeScheduleBoardTemplateNotifyModal}
						date={selectedDate}
						showModal={showScheduleBoardTemplateModal}
					/>
				}
				{isDailyView && nightShiftModalDueDate &&
					<NightShiftWorkOrdersModal
						closeModal={this._closeNightShiftModal}
						dueDate={nightShiftModalDueDate}
						workOrders={nightShiftWorkOrders}
					/>
				}
			</div>
		);
	}
}

function mapStateToProps(state: RootState) {
	const { companyData, userData } = state.user;
	const { company } = state.company;
	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const isAllowedToEditWorkOrders: boolean = isAllowed(
		PagePermissions.COMPANY.WORK_ORDERS.MANAGE,
		companyData.permissions,
		companyData.isCompanyAdmin,
		userData.role
	);
	const isAllowedToManagePerDiems: boolean = isAllowed(
		PagePermissions.COMPANY.PER_DIEMS,
		companyData.permissions,
		companyData.isCompanyAdmin,
		userData.role
	);
	const isAllowedToViewProdData: boolean = isAllowed(
		PagePermissions.COMPANY.PROD_DATA.VIEW,
		companyData.permissions,
		companyData.isCompanyAdmin,
		userData.role
	);

	const scheduleBoardOnDate = state.scheduleBoard.date ? state?.scheduleBoard?.workOrdersByDateDictionary?.[state.scheduleBoard.date] : undefined;

	const workOrders = scheduleBoardOnDate?.workOrders;
	const selectedDate = state.scheduleBoard.date ?? state.scheduleBoard.startDate ?? TimeUtils.formatDate(new Date(), TimeFormat.DATE_ONLY);
	const selectedDayOfWeek = selectedDate && TimeUtils.parseMoment(selectedDate, TimeFormat.DATE_ONLY)?.day();
	const dayBefore = selectedDayOfWeek ? selectedDayOfWeek - 1 : 6;

	return {
		dueDate: state.scheduleBoard.date ?? null,
		zoomLevel: state.scheduleBoard.zoomLevel,
		userData,
		companyData,
		workDays: company?.workDays ?? [],
		assignedPublishedNotificationStatuses: scheduleBoardOnDate?.assignedPublishedNotificationStatuses ?? undefined,
		assignedCanceledNotificationStatuses: scheduleBoardOnDate?.assignedCanceledNotificationStatuses ?? undefined,
		assignedPublishedTempLaborNotificationStatuses: scheduleBoardOnDate?.assignedPublishedTempLaborNotificationStatuses ?? null,
		assignedCanceledTempLaborNotificationStatuses: scheduleBoardOnDate?.assignedCanceledTempLaborNotificationStatuses ?? null,
		workOrders,
		participantsEmailAndSmsStatuses: state?.workOrder?.workOrders?.participantsEmailAndSmsStatuses,
		isLoading: state.http.isFetching,
		scheduleBoardView: state.scheduleBoard.scheduleBoardView,
		selectedDate,
		query: state.scheduleBoard.query,
		searchResultItems: state.scheduleBoard.searchResultItems,
		activeSearchItemIndex: state.scheduleBoard.activeSearchItemIndex,
		isMultiSelectModeActive: state.scheduleBoard.isMultiSelectModeActive,
		isShowNotesActive: state.scheduleBoard.isShowNotesActive,
		isShowAddressNotesActive: state.scheduleBoard.isShowAddressNotesActive,
		selectedWorkOrders: state?.scheduleBoard?.selectedWorkOrders,
		isAllowedToEditWorkOrders,
		isAllowedToManagePerDiems,
		isAllowedToViewProdData,
		exporting: state.http.submitting.includes(EXPORT_SCHEDULE_BOARD),
		publishing: state.http.submitting.includes(WORK_ORDER_FORM),
		notificationsDisabled: !(company?.notification?.isEnabled),
		autoNotifyOnPublish: company?.notification?.notifyOnPublish ?? null,
		autoNotifyAt: (selectedDate ? company?.notification?.notifyOnDay?.[dayBefore] : null) ?? null,
		enableScheduledNotifications: company?.notification?.enableScheduledNotifications ?? false,
		canceledWorkOrderResourceLookup: scheduleBoardOnDate?.canceledWorkOrderResourceLookups ?? undefined,
		notCanceledWorkOrderResourceLookup: scheduleBoardOnDate?.workOrderResourceLookups ?? undefined,
		employees: state?.scheduleBoard?.employees,
		temporaryEmployees: state?.scheduleBoard?.temporaryEmployees,
		dailyTip: scheduleBoardOnDate?.dailyTip,
		dailyPerDiemTip: scheduleBoardOnDate?.dailyPerDiemTip,
		initialCopyModalData: state.workOrder.copyWorkOrderModalData,
		notifyTemporaryLabor: company?.notification.notifyTemporaryLabor ?? false,
	};
}

function mapDispatchToProps() {
	return {
		updateScheduleBoardQuery: ScheduleBoardActions.updateScheduleBoardQuery,
		deactivateMultiSelectMode: ScheduleBoardActions.deactivateMultiSelectMode,
		clearFilters: ScheduleBoardActions.clearFilters,
		activateMultiSelectMode: ScheduleBoardActions.activateMultiSelectMode,
		setShowNotesMode: ScheduleBoardActions.setShowNotesMode,
		setShowAddressNotesMode: ScheduleBoardActions.setShowAddressNotesMode,
		exportScheduleBoard: ScheduleBoardActions.exportScheduleBoard,
		setActiveSearchItemIndex: ScheduleBoardActions.setActiveSearchItemIndex,
		setWorkOrdersSelection: ScheduleBoardActions.setWorkOrdersSelection,
		reinitializeCopyForm: WorkOrderActions.reinitializeCopyForm,
		findAllWorkOrdersWithNotificationsByDueDate: WorkOrderActions.findAllWorkOrdersWithNotificationsByDueDate,
		copyWorkOrder: WorkOrderActions.copyWorkOrder,
		publishWorkOrders: WorkOrderActions.publishWorkOrders,
		createDailyTip: DailyTipActions.create,
		findLastFiveDailyTips: DailyTipActions.findLastFiveDailyTips,
		createDailyPerDiemTip: DailyPerDiemTipActions.create,
		findLastFiveDailyPerDiemTips: DailyPerDiemTipActions.findLastFiveDailyPerDiemTips,
		scheduleForAutoNotify: NotifyActions.scheduleForAutoNotify,
		sendTemplateNotification: NotifyActions.sendTemplateNotificationMultipleWO,
		findNightShiftWorkOrders: ScheduleBoardActions.findNightShiftWorkOrders,
		findExpectedResultsForNotify: NotifyActions.findExpectedResultsForNotify,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentClass<OwnProps>>(
	connector,
	withRouter
);

export default enhance(ScheduleBoardHeader);
